import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import axios from "axios";

import SchoolOfArtisans from "./SchoolOfArtisans";
import SchoolOfBeauty from "./SchoolOfBeauty";
import SchoolOfDraughting from "./SchoolOfDraughting";
import SchoolOfEngineering from "./SchoolOfEngineering";
import SchoolOfGreenside from "./SchoolOfGreenside";
import SchoolOfEmendy from "./SchoolOfEmendy";
import StandardReReg from "./StandardReReg";

import SchoolOfArtisansReadOnly from "./read_only_school/SchoolOfArtisansReadOnly";
import SchoolOfBeautyReadOnly from "./read_only_school/SchoolOfBeautyReadOnly";
import SchoolOfDraughtingReadOnly from "./read_only_school/SchoolOfDraughtingReadOnly";
import SchoolOfEngineeringReadOnly from "./read_only_school/SchoolOfEngineeringReadOnly";
import SchoolOfGreensideReadOnly from "./read_only_school/SchoolOfGreensideReadOnly";
import SchoolOfEmendyReadOnly from "./read_only_school/SchoolOfEmendyReadOnly";
import StandardReRegReadOnly from "./read_only_school/StandardReRegReadOnly";

import { useDebouncedApiEffect } from "../../hooks/useDebouncedApiEffect";
import SkeletonTicketList from "../../loaders/SkeletonTicketList";

import {
  CheckProgressionSubject,
  GetProgressionSubjects,
  getQualificationDetails,
  GetRegistrationFiles,
  getReRegistrationReadOnlySubjectService,
  getReRegistrationSubjectService,
} from "../../services/ReRegistrationService";

import { buildPmcSidebarSummary } from "./pmc_rereg/shared/utils/pmcSelectionHelpers";
import ArticulationFormWrapper from "./Articulation/ArticulationFormWrapper";
import PageTabs from "./components/PageTabs";
import MobileStepper from "./components/MobileStepper";
import PMCQualificationOverview from "./pmc_rereg/PMCQualificationOverview";
import PMCRegistrationApplication from "./pmc_rereg/PMCRegistrationApplication";
import SupportingDocuments from "./SupportingDocuments";
import VideoPopup from "../video-popup/VideoPopup";
import { useBrandVideo } from "../../hooks/useBrandVideo";
import ButtonGroup from "../ui/ButtonGroup";
import Button from "../ui/Button";
import { Link } from "react-router-dom";



/**
 * Re-registration right box component
 * Handles different academy types (draughting, engineering, beauty, artisans, IT, etc.)
 * Supports PMC-driven re-registration and read-only modes
 *
 * @param {Object} props - Component props
 * @param {Object} props.data - Registration list data
 * @param {Function} props.setArticulationOption - Sets articulation option
 * @param {string} props.articulationOptionSelected - Selected articulation option
 * @param {boolean} props.graduated - Whether student has graduated
 * @param {Object} props.registrationFullData - Full registration data
 * @param {Object} props.registration - Current registration object
 * @param {Function} props.setRegistration - Updates registration state
 * @param {boolean} props.submitted - Whether form is submitted
 * @param {number|string} props.activeTab - Current active tab
 * @param {Function} props.onActiveTabChange - Tab change handler
 * @param {Function} props.onModuleSummaryChange - Module summary change handler
 * @param {Function} props.onReadOnlyDataChange - Read-only data change handler
 * @param {Function} props.setIsPmcReRegistration - Sets PMC flag
 * @param {Function} props.setCurrentStep - Sets current step
 * @param {Array} props.stepsAvailable - Available steps array
 * @param {Function} props.updateStatus - Updates registration status
 * @param {number|string} props.pmcId - PMC ID
 * @returns {JSX.Element}
*/
function ReRegistrationRightBox(props) {
  const { data: registrationList } = useSelector(state => state.registrations);
  const { qualification_higher_enrolled } = useParams();

  const {
    setArticulationOption,
    articulationOptionSelected,
    graduated,
    registrationFullData,
    registration,
    setRegistration,
    submitted,
    activeTab,
    onActiveTabChange,
    onModuleSummaryChange,
    onReadOnlyDataChange,
  } = props || {};

  const {
    isOpen,
    openVideo,
    closeVideo,
    iframeUrl,
    fallbackVideoUrl,
    hasVideo,
  } = useBrandVideo();

  const [reRegSubject, setReRegSubject] = useState([]);
  const [readOnly, setReadOnly] = useState(false);
  const [studentNewRegId, setStudentNewRegId] = useState(0);
  const [lateReg, setLateReg] = useState(0);
  const [articulationReadOnly, setArticulationReadOnly] = useState(null);
  const [subjectProgressReadOnly, setSubjectProgressReadOnly] = useState([]);
  const [subjectReWriteReadOnly, setSubjectReWriteReadOnly] = useState([]);
  const [subjectReDoReadOnly, setSubjectReDoReadOnly] = useState([]);
  const [subjectReSubReadOnly, setSubjectReSubReadOnly] = useState([]);
  const [regForReadOnly, setRegForReadOnly] = useState([]);
  const [repeatReadOnly, setRepeatReadOnly] = useState([]);
  const [electiveReadOnly, setElectiveReadOnly] = useState([]);
  const [delivery_methode, setDelivery_methode] = useState("");
  const [isDataInBachleor, setIsDataInBachleor] = useState(true);
  const [isPmcReRegistration, setIsPmcReRegistration] = useState(false);
  const [pmcFormData, setPmcFormData] = useState(null);
  const [readOnlyData, setReadOnlyData] = useState({});
  const [loading, setLoading] = useState(true);
  const [qualificationDetails, setQualificationDetails] = useState({});
  const [overviewQualification, setOverviewQualification] = useState(null);
  const [overviewModules, setOverviewModules] = useState([]);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [outstandingFilesCount, setOutstandingFilesCount] = useState(0);
  const [files, setFiles] = useState([]);
  const [reloadFiles, setReloadFiles] = useState(false);
  const [foundation, setFoundation] = useState(false);
  const [student_type, setStudent_type] = useState(-1);
  const [moduleSummaryData, setModuleSummaryData] = useState(null);

  const checkFormRequestIdRef = useRef(0);
  const readOnlyRequestRegIdRef = useRef(null);
  const completionSnapshotRef = useRef({
    step1Complete: null,
    step2Complete: null,
    hasSupportingDocs: null,
  });
  const hasAppliedDefaultNavigationRef = useRef(false);
  const userManuallyChangedTabRef = useRef(false);
  const lastAutoSetTabRef = useRef(null);

  const userData = JSON.parse(localStorage.getItem("user"));

  /**
   * Calculates current step completion state for PMC re-registration
   * Determines if step 1 is complete (submitted status), step 2 is complete (account payer updated),
   * and if supporting documents are needed based on files data
   * @returns {{isStep1Complete: boolean, isStep2Complete: boolean, hasSupportingDocs: boolean}} Step completion state
  */
  const stepState = useMemo(() => {
    const statusValue = (readOnlyData?.status ?? registration?.status ?? "").trim().toLowerCase();
    // account_payer_updated is on registration object, not in readOnlyData
    const accountPayerUpdatedValue = Number(registration?.account_payer_updated || 0);

    const isStep1Complete = statusValue === "submitted" || Boolean(submitted);
    const isStep2Complete = accountPayerUpdatedValue === 1;

    const filesNeeded = readOnlyData?.filesNeeded || [];
    const hasActiveRequiredFile = filesNeeded.length > 0 && filesNeeded.some(file => file?.active === 1);
    const hasFiles = (files && files.length > 0) || filesNeeded.length > 0;
    const hasSupportingDocs =
      hasActiveRequiredFile ||
      outstandingFilesCount > 0 ||
      hasFiles;

    return {
      isStep1Complete,
      isStep2Complete,
      hasSupportingDocs,
    };
  }, [
    readOnlyData,
    registration?.status,
    registration?.account_payer_updated,
    submitted,
    outstandingFilesCount,
    files,
  ]);

  /**
   * Determines if a payload is PMC-driven based on pmc_id and netsuite_id
   * @param {Object} payload - The payload to check
   * @returns {boolean} Whether the payload is PMC-driven
  */
  const isPmcDriven = payload => {
    if (!payload) return false;
    const pmcId = Number(payload.pmc_id || 0);
    const netsuiteId = Number(payload.netsuite_id || 0);
    return pmcId > 0 && netsuiteId > 0;
  };


  /**
   * Resets all component state to initial values when registration ID changes
   * Ensures clean state for new registration selection
  */
  useEffect(() => {
    setReadOnly(false);
    setReadOnlyData({});
    setIsDataInBachleor(true);
    setStudentNewRegId(0);
    setLateReg(0);
    setArticulationReadOnly(null);
    setSubjectProgressReadOnly([]);
    setSubjectReWriteReadOnly([]);
    setSubjectReDoReadOnly([]);
    setSubjectReSubReadOnly([]);
    setRegForReadOnly([]);
    setRepeatReadOnly([]);
    setElectiveReadOnly([]);
    setFoundation(false);
    setStudent_type(-1);
    setDelivery_methode("");
    setOverviewQualification(null);
    setOverviewModules([]);
    setOutstandingFilesCount(0);
    setLoading(true);
    setIsPmcReRegistration(false);
    setPmcFormData(null);
    checkFormRequestIdRef.current += 1;
    readOnlyRequestRegIdRef.current = null;
    completionSnapshotRef.current = {
      step1Complete: null,
      step2Complete: null,
      hasSupportingDocs: null,
    };
    hasAppliedDefaultNavigationRef.current = false;
  }, [registration?.id]);


  /**
   * Checks form progression status and determines if registration is PMC-driven
   * Handles race conditions using request ID tracking
   * Falls back to read-only payload on API error when read-only mode is active
   * @param {number|string} idToSend - Qualification assign ID to check
   * @param {boolean} isReadOnly - Whether currently in read-only mode
   * @param {Object} readOnlyPayload - Read-only data payload for fallback
  */
  const checkForm = (idToSend, isReadOnly, readOnlyPayload) => {
    const requestId = ++checkFormRequestIdRef.current;
    setLoading(true);

    const qualificationAssignId = Number(idToSend);
    if (!qualificationAssignId || Number.isNaN(qualificationAssignId)) {
      setIsPmcReRegistration(false);
      setPmcFormData(null);
      setLoading(false);
      return;
    }

    CheckProgressionSubject({ qualification_assign_id: qualificationAssignId })
      .then(res => {
        if (requestId !== checkFormRequestIdRef.current) return;

        setLoading(false);

        const apiIsPmc = isPmcDriven(res?.data);
        const readOnlyIsPmc = isReadOnly && readOnlyPayload ? isPmcDriven(readOnlyPayload) : false;
        const isPmc = apiIsPmc || readOnlyIsPmc;

        setIsPmcReRegistration(isPmc);
        setPmcFormData(isPmc ? (res?.data || readOnlyPayload) : null);

        if (props?.setIsPmcReRegistration) {
          props.setIsPmcReRegistration(Boolean(isPmc));
        }
      })
      .catch(err => {
        if (requestId !== checkFormRequestIdRef.current) return;

        setLoading(false);

        if (isReadOnly && readOnlyPayload && Object.keys(readOnlyPayload).length > 0) {
          const readOnlyIsPmc = isPmcDriven(readOnlyPayload);
          setIsPmcReRegistration(readOnlyIsPmc);
          setPmcFormData(readOnlyIsPmc ? readOnlyPayload : null);

          if (props?.setIsPmcReRegistration) {
            props.setIsPmcReRegistration(Boolean(readOnlyIsPmc));
          }
        } else {
          setIsPmcReRegistration(false);
          setPmcFormData(null);
        }

        console.log(err);
      });
  };


  /**
   * Triggers form progression check when read-only mode, data, or registration changes
  */
  useEffect(() => {
    checkForm(qualification_higher_enrolled, readOnly, readOnlyData);
  }, [readOnly, readOnlyData, qualification_higher_enrolled, registration?.id]);

  /**
   * Ensures steps 1 and 2 are always available for PMC re-registration in non-read-only mode
   * Removes step 4 if present and preserves step 5 when available
  */
  useEffect(() => {
    if (!readOnly && isPmcReRegistration && props?.setStepsAvailable) {
      const currentSteps = props.stepsAvailable || [];
      // Ensure steps 1 and 2 are present, remove step 4 if present
      if (!currentSteps.includes(1) || !currentSteps.includes(2) || currentSteps.includes(4)) {
        const updatedSteps = [1, 2];
        // Preserve step 5 if it exists and is valid
        if (currentSteps.includes(5)) {
          updatedSteps.push(5);
        }
        props.setStepsAvailable(updatedSteps);
        hasAppliedDefaultNavigationRef.current = false;
      }
    }
  }, [readOnly, isPmcReRegistration, props?.stepsAvailable, props?.setStepsAvailable]);

  /**
   * Adds step 5 (supporting documents) to available steps when outstanding files exist
   * Only applies to PMC re-registration mode
  */
  useEffect(() => {
    if (!isPmcReRegistration || !props?.setStepsAvailable) {
      return;
    }

    if (outstandingFilesCount > 0) {
      const currentSteps = props.stepsAvailable || [];
      if (!currentSteps.includes(5)) {
        const updated = [...currentSteps.filter(step => step !== 4), 5].sort((a, b) => a - b);
        props.setStepsAvailable(updated);
        hasAppliedDefaultNavigationRef.current = false;
      }
    }
  }, [isPmcReRegistration, outstandingFilesCount, props?.stepsAvailable, props?.setStepsAvailable]);

  /**
   * Fetches re-registration subjects and late registration flag
   * Triggers when registration list opens or registration ID changes
  */
  useEffect(() => {
    if (!props?.registration?.id) return;

    getReRegistrationSubjectService(props.registration.id).then(res => {
      setReRegSubject(res.data.result);
      setLateReg(res.data.late_reg);
    });
  }, [registrationList.open, props?.registration?.id]);

  /**
   * Fetches qualification details when enrolled qualification ID is valid
  */
  useEffect(() => {
    if (qualification_higher_enrolled > 0) {
      getQualificationDetails(qualification_higher_enrolled).then(res => {
        setQualificationDetails(res?.data);
      });
    }
  }, [qualification_higher_enrolled]);


  /**
   * Fetches read-only re-registration subject data from the server
   * Handles PMC and non-PMC step configuration based on response data
   * Updates articulation, subjects, registration data, and available steps
  */
  const fetchReRegistrationReadOnlySubjectService = async () => {
    if (
      readOnly ||
      props.registration.readOnly ||
      props.registration.studentNewRegId > 0
    ) {
      const regId = props.registration.studentNewRegId || studentNewRegId;

      readOnlyRequestRegIdRef.current = regId;

      const res = await getReRegistrationReadOnlySubjectService(regId);
      if (!res || readOnlyRequestRegIdRef.current !== regId) return;

      const data = res.data;
      const filesNeeded = data?.filesNeeded || null;

      let availableStepsTemp = props.stepsAvailable || [];
      setArticulationReadOnly({
        campus: data?.campus_selected,
        building: data?.building_id,
        learningMethod: data?.delivery_methode,
        progression: data?.progression,
      });

      setSubjectProgressReadOnly(data?.subjectProgress_);
      setSubjectReWriteReadOnly(data?.subjectRewrite_);
      setSubjectReDoReadOnly(data?.subjectRedo_);
      setSubjectReSubReadOnly(data?.subjectReSub_);
      setDelivery_methode(data?.delivery_methode);
      setRegForReadOnly(data?.registerFor);
      setRepeatReadOnly(data?.repeat);
      setElectiveReadOnly(data?.elective);
      setReadOnlyData(data);
      
      // Pass readOnlyData to parent for sidebar step state
      if (onReadOnlyDataChange) {
        onReadOnlyDataChange(data);
      }

      const isPmcFromData = isPmcDriven(data);
      const isPmcFromRegistration = isPmcDriven(props.registration);
      const hasPmcSteps =
        availableStepsTemp.includes(1) &&
        availableStepsTemp.includes(2) &&
        !availableStepsTemp.includes(4);

      const isPmc = isPmcFromData || isPmcFromRegistration || hasPmcSteps;

      if (isPmc) {
        // Ensure steps 1 and 2 are always present for PMC, remove step 4 if present
        if (!availableStepsTemp.includes(1) || !availableStepsTemp.includes(2)) {
          availableStepsTemp = [1, 2];
        }
        
        // Remove step 4 if it exists (should never be in PMC steps)
        availableStepsTemp = availableStepsTemp.filter(step => step !== 4);

        const hasFileRequired =
          filesNeeded?.length > 0 && filesNeeded.some(file => file.active === 1);

        if (hasFileRequired && !availableStepsTemp.includes(5)) {
          availableStepsTemp = [...availableStepsTemp, 5].sort((a, b) => a - b);
        }

        props.setStepsAvailable(availableStepsTemp);
        hasAppliedDefaultNavigationRef.current = false;
      } else {
        const hasPmcStepsNonPmc =
          availableStepsTemp.includes(1) &&
          availableStepsTemp.includes(2) &&
          !availableStepsTemp.includes(4);

        if (hasPmcStepsNonPmc) return;

        const perValue = data?.per;

        if (
          filesNeeded?.length > 0 &&
          perValue !== "80%" &&
          perValue !== "90%" &&
          perValue !== "95%" &&
          perValue !== "100%"
        ) {
          const hasFileRequired = filesNeeded.some(file => file.active === 1);

          if (hasFileRequired) {
            props.setCurrentStep(5);

            if (!availableStepsTemp.includes(5)) {
              availableStepsTemp = [...availableStepsTemp, 5];
            }

            availableStepsTemp.sort((a, b) => a - b);
            props.setStepsAvailable(availableStepsTemp);
          } else if (availableStepsTemp.includes(4)) {
            props.setCurrentStep(4);
          }
        } else if (availableStepsTemp.includes(4)) {
          props.setCurrentStep(4);
        }
      }

      setFoundation(data?.isFoundation);
      setStudent_type(data?.student_type ?? -1);
      setReadOnly(true);
      props.setSubmitted(true);
    }
  };

  useDebouncedApiEffect(fetchReRegistrationReadOnlySubjectService, [readOnly]);


  /**
   * Fetches registration files when student has a valid new registration ID
   * Calculates outstanding (unuploaded) files count
  */
  useEffect(() => {
    if (readOnly && readOnlyData && Object.keys(readOnlyData).length > 0) {
      const readOnlyIsPmc = isPmcDriven(readOnlyData);

      if (readOnlyIsPmc) {
        setIsPmcReRegistration(true);
        setPmcFormData(readOnlyData);

        if (props?.setIsPmcReRegistration) {
          props.setIsPmcReRegistration(true);
        }
      }
    }
  }, [readOnly, readOnlyData, props]);


  /**
   * Fetches registration files when student has a valid new registration ID
   * Calculates outstanding (unuploaded) files count
  */
  useEffect(() => {
    if (registration?.studentNewRegId > 0) {
      GetRegistrationFiles(registration?.studentNewRegId).then((res) => {
        const filesNeeded = res?.data || []
        if (filesNeeded) {
          let filterUploaded = filesNeeded && filesNeeded.filter(file => file.uploaded === 0) || [];
          setOutstandingFilesCount(filterUploaded.length);
          setFiles(filesNeeded);
        }
      })
    } else {
      setOutstandingFilesCount(0)
    }
  }, [registration?.studentNewRegId]);


  /**
   * Reloads registration files when reloadFiles flag is triggered
   * Updates outstanding files count based on uploaded status
  */
  useEffect(() => {
    if(reloadFiles == true){
      GetRegistrationFiles(registration?.studentNewRegId).then((res) => {
        const filesNeeded = res?.data || []
        if (filesNeeded) {
          let filterUploaded = filesNeeded && filesNeeded.filter(file => file.uploaded === 0) || [];
          setOutstandingFilesCount(filterUploaded.length);
          setFiles(filesNeeded);
        }
      });
    }
  }, [reloadFiles])

  /**
    * Fetches progression subjects overview for PMC qualification
    * @param {Object} options - Fetch options
    * @param {Object} options.cancelToken - Axios cancel token for request cancellation
  */
  const fetchProgressionSubjectsOverview = async ({ cancelToken }) => {
    const formData = pmcFormData;

    if (
      !isPmcReRegistration ||
      !formData ||
      !formData.netsuite_id ||
      !userData ||
      !userData.id
    ) {
      setOverviewQualification(null);
      setOverviewModules([]);
      return;
    }

    const data = {
      studentId: userData.id,
      netsuiteProductId: formData.netsuite_id,
    };

    setOverviewLoading(true);

    try {
      const res = await GetProgressionSubjects(data, { cancelToken });

      if (res?.data?.responseData) {
        const responseData = res.data.responseData;
        const qualification = responseData.qualification || { name: "" };
        const allModules = responseData.AllModules || [];

        setOverviewQualification(qualification);
        setOverviewModules(allModules);

        if (onModuleSummaryChange) {
          const summary = buildPmcSidebarSummary({
            responseData,
            qualificationOverride: qualification,
            readOnly,
            registration,
            readOnlyData,
          });

          if (summary) {
            onModuleSummaryChange(summary);
          }
        }
      }
    } catch (err) {
      if (!axios.isCancel(err)) {
        console.error(err);
      }
    } finally {
      setOverviewLoading(false);
    }
  };

  useDebouncedApiEffect(fetchProgressionSubjectsOverview, [
    isPmcReRegistration,
    pmcFormData,
    readOnly,
    readOnlyData,
  ]);

  /**
   * Handles account payer step completion
   * Updates registration state and navigates to application tab
 */
  const handleAccountPayerCompleted = () => {
    if (setRegistration && registration) {
      setRegistration({ ...registration, account_payer_updated: 1 });
    }

    if (props?.setCurrentStep) {
      props.setCurrentStep(1, { skipTabSync: true });
    }

    if (onActiveTabChange) {
      onActiveTabChange("application");
    }

    hasAppliedDefaultNavigationRef.current = false;
  };


  /**
   * Handles module summary data changes
   * @param {Object} summary - Module summary data
 */
  const handleModuleSummaryChange = useCallback((summary) => {
    setModuleSummaryData(summary);
    if (onModuleSummaryChange) {
      onModuleSummaryChange(summary);
    }
  }, [onModuleSummaryChange]);


  /**
    * Renders the application form content based on academy type and mode
    * @returns {JSX.Element} Application content
  */
  const renderApplicationContent = () => {
    const pmcData = pmcFormData;

    return (
      <>
        {isPmcReRegistration && !loading && pmcData && (
          <PMCRegistrationApplication
            lateReg={lateReg}
            reRegSubject={reRegSubject}
            bachleorData={pmcData}
            readOnlyData={readOnlyData}
            qualificationDetails={qualificationDetails}
            isData={isDataInBachleor}
            setIsData={setIsDataInBachleor}
            registration={registration}
            readOnly={readOnly}
            setReadOnly={setReadOnly}
            setStudentNewRegId={setStudentNewRegId}
            updateStatus={props?.updateStatus}
            setCurrentStep={props?.setCurrentStep}
            setStepsAvailable={props?.setStepsAvailable}
            currentStep={props?.currentStep}
            stepsAvailable={props?.stepsAvailable}
            setSubmitted={props?.setSubmitted}
            onModuleSummaryChange={handleModuleSummaryChange}
            onAccountPayerComplete={handleAccountPayerCompleted}
            setRegistration={setRegistration}
          />
        )}

        <div className="re-registration-register-bx">
          <div className="card card-form-bx">
            {loading && <SkeletonTicketList />}

            {!readOnly &&
              !loading &&
              !isPmcReRegistration &&
              registration &&
              registration.academy === "draughting" && (
                <SchoolOfDraughting
                  lateReg={lateReg}
                  reRegSubject={reRegSubject}
                  registration={registration}
                  readOnly={readOnly}
                  setReadOnly={setReadOnly}
                  setStudentNewRegId={setStudentNewRegId}
                  updateStatus={props.updateStatus}
                />
              )}

            {!readOnly &&
              !loading &&
              !isPmcReRegistration &&
              registration &&
              registration.academy === "engineering" && (
                <SchoolOfEngineering
                  lateReg={lateReg}
                  reRegSubject={reRegSubject}
                  registration={registration}
                  readOnly={readOnly}
                  setReadOnly={setReadOnly}
                  setStudentNewRegId={setStudentNewRegId}
                  updateStatus={props.updateStatus}
                />
              )}

            {!readOnly &&
              !loading &&
              !isPmcReRegistration &&
              registration &&
              (registration.academy === "gdc"
                || registration.academy === "visual_art"
                || registration.academy === "visual_design"
              ) && (
                <SchoolOfGreenside
                  lateReg={lateReg}
                  reRegSubject={reRegSubject}
                  registration={registration}
                  readOnly={readOnly}
                  setReadOnly={setReadOnly}
                  setStudentNewRegId={setStudentNewRegId}
                  updateStatus={props.updateStatus}
                />
              )}

            {!readOnly &&
              !loading &&
              !isPmcReRegistration &&
              registration &&
              (registration.academy === "IT" ||
                registration.academy === "architecture" ||
                registration.academy === "business") && (
                <StandardReReg
                  lateReg={lateReg}
                  reRegSubject={reRegSubject}
                  registration={registration}
                  readOnly={readOnly}
                  qualificationDetails={qualificationDetails}
                  setReadOnly={setReadOnly}
                  setStudentNewRegId={setStudentNewRegId}
                  updateStatus={props.updateStatus}
                />
              )}

            {!readOnly &&
              !loading &&
              !isPmcReRegistration &&
              registration &&
              registration.academy === "beauty" && (
                <SchoolOfBeauty
                  lateReg={lateReg}
                  reRegSubject={reRegSubject}
                  registration={registration}
                  readOnly={readOnly}
                  setReadOnly={setReadOnly}
                  setStudentNewRegId={setStudentNewRegId}
                  updateStatus={props.updateStatus}
                />
              )}

            {!readOnly &&
              !loading &&
              !isPmcReRegistration &&
              registration &&
              registration.academy === "artisans" && (
                <SchoolOfArtisans
                  lateReg={lateReg}
                  reRegSubject={reRegSubject}
                  registration={registration}
                  readOnly={readOnly}
                  setReadOnly={setReadOnly}
                  setStudentNewRegId={setStudentNewRegId}
                  updateStatus={props.updateStatus}
                />
              )}

            {!readOnly &&
              !loading &&
              !isPmcReRegistration &&
              registration &&
              (registration.academy === "fst" ||
                registration.academy === "fg") && (
                <SchoolOfEmendy
                  lateReg={lateReg}
                  reRegSubject={reRegSubject}
                  registration={registration}
                  readOnly={readOnly}
                  setReadOnly={setReadOnly}
                  setStudentNewRegId={setStudentNewRegId}
                  updateStatus={props.updateStatus}
                />
              )}

            {readOnly &&
              !isPmcReRegistration &&
              !loading &&
              registration &&
              registration.academy === "draughting" && (
                <SchoolOfDraughtingReadOnly
                  readOnlyData={readOnlyData}
                  reRegSubject={reRegSubject}
                  registration={registration}
                  subjectProgressReadOnly={subjectProgressReadOnly}
                  subjectReWriteReadOnly={subjectReWriteReadOnly}
                  subjectReDoReadOnly={subjectReDoReadOnly}
                  subjectReSubReadOnly={subjectReSubReadOnly}
                  delivery_methode={delivery_methode}
                />
              )}

            {readOnly &&
              !isPmcReRegistration &&
              !loading &&
              registration &&
              registration.academy === "engineering" && (
                <SchoolOfEngineeringReadOnly
                  readOnlyData={readOnlyData}
                  reRegSubject={reRegSubject}
                  registration={registration}
                  foundation={foundation}
                  subjectProgressReadOnly={subjectProgressReadOnly}
                  subjectReWriteReadOnly={subjectReWriteReadOnly}
                  subjectReDoReadOnly={subjectReDoReadOnly}
                  subjectReSubReadOnly={subjectReSubReadOnly}
                  student_type={student_type}
                />
              )}

            {readOnly &&
              !isPmcReRegistration &&
              !loading &&
              registration &&
              registration.academy === "beauty" && (
                <SchoolOfBeautyReadOnly
                  readOnlyData={readOnlyData}
                  reRegSubject={reRegSubject}
                  registration={registration}
                  subjectReDoReadOnly={subjectReDoReadOnly}
                  student_type={student_type}
                />
              )}

            {readOnly &&
              !isPmcReRegistration &&
              !loading &&
              registration &&
              registration.academy === "artisans" && (
                <SchoolOfArtisansReadOnly
                  reRegSubject={reRegSubject}
                  registration={registration}
                  subjectReDoReadOnly={subjectReDoReadOnly}
                  readOnlyData={readOnlyData}
                  student_type={student_type}
                />
              )}

            {readOnly &&
              !isPmcReRegistration &&
              !loading &&
              registration &&
              (registration.academy === "IT" ||
                registration.academy === "architecture" ||
                registration.academy === "business") && (
                <StandardReRegReadOnly
                  readOnlyData={readOnlyData}
                  reRegSubject={reRegSubject}
                  registration={registration}
                  foundation={foundation}
                  subjectProgressReadOnly={subjectProgressReadOnly}
                  subjectReDoReadOnly={subjectReDoReadOnly}
                  student_type={student_type}
                />
              )}

            {readOnly &&
              !isPmcReRegistration &&
              !loading &&
              registration &&
              (registration.academy === "gdc"
                || registration.academy === "visual_art"
                || registration.academy === "visual_design"
              ) && (
                <SchoolOfGreensideReadOnly
                  readOnlyData={readOnlyData}
                  reRegSubject={reRegSubject}
                  registration={registration}
                  regForReadOnly={regForReadOnly}
                  repeatReadOnly={repeatReadOnly}
                  student_type={student_type}
                />
              )}

            {readOnly &&
              !isPmcReRegistration &&
              !loading &&
              registration &&
              (registration.academy === "fst" ||
                registration.academy === "fg") && (
                <SchoolOfEmendyReadOnly
                  readOnlyData={readOnlyData}
                  reRegSubject={reRegSubject}
                  registration={registration}
                  regForReadOnly={regForReadOnly}
                  repeatReadOnly={repeatReadOnly}
                  electiveReadOnly={electiveReadOnly}
                  student_type={student_type}
                />
              )}
          </div>
        </div>
      </>
    );
  };


  /**
   * Calculates formatted deposit amount from readOnlyData or module summary totals
   * Uses readOnlyData deposit if available, otherwise calculates from modules total minus adjustments
   * Returns formatted South African Rand string or undefined
   * @returns {string|undefined} Formatted deposit amount with currency symbol
  */
  const depositAmountFormatted = useMemo(() => {
    // Use readOnlyData deposit if available
    if (readOnlyData?.depositAmount !== undefined && readOnlyData?.depositAmount !== null) {
      const value = Number(readOnlyData.depositAmount);
      return `R ${value.toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    
    // Calculate from module totals
    if (moduleSummaryData?.modulesTotal !== undefined && moduleSummaryData?.adjustmentsTotal !== undefined) {
      const total = Number(moduleSummaryData.modulesTotal) - Number(moduleSummaryData.adjustmentsTotal);
      const deposit = total > 0 ? (total >= 14000 ? 7000 : Number((total * 0.5).toFixed(2))) : 0;
      return `R ${deposit.toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    
    return undefined;
  }, [readOnlyData?.depositAmount, moduleSummaryData?.modulesTotal, moduleSummaryData?.adjustmentsTotal]);

  /**
   * Calculates the supporting documents badge count
   * Prioritizes outstandingFilesCount state, falls back to files array, then readOnlyData
   * Counts only active files that haven't been uploaded
   * @returns {number} Count of outstanding documents needing upload
  */
  const supportingDocsBadge = useMemo(() => {
    // First, try to use outstandingFilesCount (from state)
    if (outstandingFilesCount > 0) {
      return outstandingFilesCount;
    }
    
    // Otherwise, calculate from files state (most up-to-date)
    if (files && files.length > 0) {
      const outstanding = files.filter(file => file?.active === 1 && file?.uploaded === 0);
      if (outstanding.length > 0) {
        return outstanding.length;
      }
    }
    
    // Fallback to readOnlyData if files state is not available
    if (readOnlyData?.filesNeeded && readOnlyData.filesNeeded.length > 0) {
      const outstanding = readOnlyData.filesNeeded.filter(file => file?.active === 1 && file?.uploaded === 0);
      if (outstanding.length > 0) {
        return outstanding.length;
      }
    }
    
    return 0;
  }, [outstandingFilesCount, files, readOnlyData?.filesNeeded]);

  /**
  * Builds tabs array based on registration state and step completion
  * Graduated students get only application tab with articulation form
  * PMC registrations include overview, application, and supporting documents tabs as needed
  * @returns {Array<{id: string, label: string, title: string, icon: string, content: JSX.Element, badge?: number}>} Tab configuration array
 */
  const tabs = (() => {
    if (graduated) {
      return [
        {
          id: "application",
          label: "Application",
          title: "Application",
          icon: "fal fa-edit",
          content: (
            <ArticulationFormWrapper
              articulationOptionSelected={articulationOptionSelected}
              articulationReadOnly={articulationReadOnly}
              currentStep={props.currentStep}
              pmcId={props.pmcId}
              setArticulationOption={setArticulationOption}
              qualificationDetails={qualificationDetails}
              setCurrentStep={props.setCurrentStep}
              payloadData={{ lateReg, registration, registrationFullData }}
            />
          ),
        },
      ];
    }

    const { isStep1Complete, hasSupportingDocs } = stepState;

    const tabsArray = [
      {
        id: "overview",
        label: "Overview",
        title: "Overview",
        icon: "fal fa-info-circle",
        content: (
          <PMCQualificationOverview
            qualification={overviewQualification}
            modules={overviewModules}
            isLoading={overviewLoading}
          />
        ),
      },
      {
        id: "application",
        label: "Application",
        title: "Application",
        icon: "fal fa-edit",
        content: renderApplicationContent(),
      },
    ];

    // Show supporting documents tab if docs are required/present
    if (hasSupportingDocs || isStep1Complete) {
      tabsArray.push({
        id: "supporting_documents",
        label: "Supporting Documents",
        title: "Supporting Documents",
        icon: "fal fa-folder-open",
        badge: supportingDocsBadge,
        content: (
          <SupportingDocuments
            regId={registration?.studentNewRegId}
            registrationId={registration?.id}
            files={files}
            setReloadFiles={setReloadFiles}
            downloads={readOnlyData?.filesDownloads || []}
            setRegistration={setRegistration}
            activeTab={activeTab}
            onModuleSummaryChange={handleModuleSummaryChange}
            {...(depositAmountFormatted && {
              depositAmount: depositAmountFormatted,
            })}
            studentNumber={userData?.student_crm_id || ""}
          />
        ),
      });
    }

    return tabsArray;
  })();


  /**
   * Handles tab change events and tracks manual user tab selection
   * Sets manual change flag to prevent auto-navigation from overriding user choice
   * @param {string} tabId - ID of the selected tab
  */
  const handleTabChange = tabId => {
    // Mark that user manually changed the tab
    userManuallyChangedTabRef.current = true;
    if (onActiveTabChange) {
      onActiveTabChange(tabId);
    }
  };


  /**
   * Determines the default tab based on registration state
   * Returns application tab for graduated students and non-PMC registrations
   * For PMC: overview (steps incomplete), application (step 2 pending), 
   * supporting_documents (both steps complete with files needed)
   * @returns {string} Default tab ID
  */
  const getDefaultTab = () => {
    if (graduated) {
      return "application";
    }

    if (!isPmcReRegistration) {
      return "application";
    }

    const { isStep1Complete, isStep2Complete, hasSupportingDocs } = stepState;

    if (!isStep1Complete && !isStep2Complete) {
      return "overview";
    }

    // When both steps are complete AND there are required documents, go to supporting documents
    if (isStep1Complete && isStep2Complete && hasSupportingDocs) {
      return "supporting_documents";
    }


    if (isStep1Complete && isStep2Complete) {
      return "application";
    }

    return "application";
  };


  /**
   * Updates available steps configuration for PMC read-only mode
   * Ensures steps 1 and 2 are always present for PMC, removes step 4 if present
   * Adds step 5 (supporting documents) when active files are required
   * Includes change detection to prevent unnecessary updates and infinite loops
  */
  useEffect(() => {
    if (readOnly && isPmcReRegistration && readOnlyData && Object.keys(readOnlyData).length > 0) {
      const isPmc = isPmcDriven(readOnlyData);
      if (!isPmc) return;

      const currentStepsAvailable = props.stepsAvailable || [];
      // Ensure steps 1 and 2 are always present for PMC, remove step 4 if present
      let updatedSteps = [1, 2];
      
      // Add step 5 if files are required
      const filesNeeded = readOnlyData?.filesNeeded || [];
      const hasFileRequired =
        filesNeeded.length > 0 && filesNeeded.some(file => file.active === 1);
      
      if (hasFileRequired && !updatedSteps.includes(5)) {
        updatedSteps = [...updatedSteps, 5];
      }
      
      // Only update if steps have changed (to avoid infinite loops)
      const stepsChanged = 
        updatedSteps.length !== currentStepsAvailable.length ||
        !updatedSteps.every(step => currentStepsAvailable.includes(step)) ||
        currentStepsAvailable.includes(4); // Always update if step 4 is present (should be removed)
      
      if (stepsChanged) {
        props.setStepsAvailable(updatedSteps);
      }

      hasAppliedDefaultNavigationRef.current = false;
    }
  }, [
    readOnly,
    isPmcReRegistration,
    readOnlyData,
    registration?.studentNewRegId,
    props?.currentStep,
    props?.stepsAvailable,
    activeTab,
    onActiveTabChange,
    props,
  ]);

  // Detect when user manually changes tab (tab doesn't match expected tab for current step state)
  useEffect(() => {
    if (!isPmcReRegistration || !activeTab) {
      return;
    }

    const { isStep1Complete, isStep2Complete, hasSupportingDocs } = stepState;

    let expectedTab = "application";
    if (!isStep1Complete && !isStep2Complete) {
      expectedTab = "overview";
    } else if (isStep1Complete && !isStep2Complete) {
      expectedTab = "application";
    } else if (isStep1Complete && isStep2Complete && hasSupportingDocs) {
      expectedTab = "supporting_documents";
    } else {
      expectedTab = "application";
    }

    // If current tab doesn't match expected tab AND it's not the last auto-set tab,
    if (activeTab !== expectedTab && activeTab !== lastAutoSetTabRef.current && hasAppliedDefaultNavigationRef.current) {
      userManuallyChangedTabRef.current = true;
    }
  }, [isPmcReRegistration, activeTab, stepState.isStep1Complete, stepState.isStep2Complete, stepState.hasSupportingDocs]);

  useEffect(() => {
    if (!isPmcReRegistration) {
      return;
    }

    const { isStep1Complete, isStep2Complete, hasSupportingDocs } = stepState;

    const snapshot = completionSnapshotRef.current;
    const completionChanged =
      snapshot.step1Complete !== isStep1Complete ||
      snapshot.step2Complete !== isStep2Complete ||
      snapshot.hasSupportingDocs !== hasSupportingDocs;

    if (completionChanged) {
      completionSnapshotRef.current = {
        step1Complete: isStep1Complete,
        step2Complete: isStep2Complete,
        hasSupportingDocs,
      };
      hasAppliedDefaultNavigationRef.current = false;
      // Reset manual tab change flag when step completion changes. This allows auto-navigation to work when steps complete
      userManuallyChangedTabRef.current = false;
    }

    // If user manually changed the tab, don't override it unless step completion changed
    if (userManuallyChangedTabRef.current && !completionChanged) {
      return;
    }

    if (hasAppliedDefaultNavigationRef.current && !completionChanged) {
      return;
    }

    let targetStep = 1;
    let expectedTab = "application";

    if (!isStep1Complete && !isStep2Complete) {
      targetStep = 1;
      expectedTab = "overview";
    } else if (isStep1Complete && !isStep2Complete) {
      targetStep = 2;
      expectedTab = "application";
    } else if (isStep1Complete && isStep2Complete && hasSupportingDocs) {
      // When both steps are complete AND there are required documents, go to supporting documents
      targetStep = 5;
      expectedTab = "supporting_documents";
    } else if (isStep1Complete && isStep2Complete) {
      // When both steps are complete but no required documents, default to step 1 on application tab
      targetStep = 1;
      expectedTab = "application";
    } else {
      targetStep = 1;
      expectedTab = "application";
    }

    if (props.setCurrentStep && props.currentStep !== targetStep) {
      props.setCurrentStep(targetStep, { skipTabSync: true });
    }

    if (onActiveTabChange && activeTab !== expectedTab) {
      onActiveTabChange(expectedTab);
      lastAutoSetTabRef.current = expectedTab;
    }

    hasAppliedDefaultNavigationRef.current = true;
  }, [
    isPmcReRegistration,
    stepState.isStep1Complete,
    stepState.isStep2Complete,
    stepState.hasSupportingDocs,
    props.setCurrentStep,
    props.currentStep,
    activeTab,
    onActiveTabChange,
  ]);

  /**
   * Determines page content rendering based on PMC mode
   * PMC registrations render with PageTabs and MobileStepper navigation
   * Non-PMC registrations render application content directly
   * @returns {JSX.Element} Page content to render
  */
  const RenderPageContent = isPmcReRegistration ? (
    <PageTabs
      tabs={tabs}
      defaultTab={getDefaultTab()}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      mobileStepper={
        <MobileStepper
          currentStep={props?.currentStep}
          stepsAvailable={props?.stepsAvailable || []}
          submitted={submitted}
          activeTab={activeTab}
          onStepClick={props?.setCurrentStep}
          onTabChange={handleTabChange}
          readOnlyData={readOnlyData}
          registration={registration}
        />
      }
    />
  ) : (
    renderApplicationContent()
  );

  return (
    <div className="rgt-wrapper">
      <div className="re-registration-detail-rgt pmc-rgt">
        <div className="re-registration-detail-top gap-2 d-flex justify-content-between">
          <div className="re-registration-detail-top-left">
            <div className="re-registration-bx-info d-flex">
              <div className="icon">SA</div>
              <div className="re-registration-bx-info-rgt">
                <h3>
                  {registration?.academy2}, Intake {registration?.intake_number},{" "}
                  {registration?.intake_year}
                </h3>
                <p>
                  <i className="fal fa-calendar-days" /> {registration?.start_date} to{" "}
                  {registration?.end_date}
                </p>
              </div>
            </div>
          </div>

          <ButtonGroup className="re-registration-detail-top-right mt-0 show-shadow">
            {(
              (!stepState?.isStep1Complete || !stepState?.isStep2Complete) &&
              Number(pmcFormData?.pmc_id) > 0 &&
              hasVideo
            ) && <Button
              type="button"
              variant="white"
              className="hide-mobile"
              onClick={openVideo}
            >
              <i className="fal fa-question-circle" />

              How to Re-Register
            </Button>}
            <Button
              as={Link}
              title="Back"
              variant="white"
              className="hide-mobile"
              to="/reregistration"
            >
              <i className="fal fa-angle-left" />
              Back
            </Button>
          </ButtonGroup>
        </div>

        {RenderPageContent}
        {isOpen && <VideoPopup
          open={isOpen}
          onOpenChange={closeVideo}
          iframe={iframeUrl}
          videoUrl={fallbackVideoUrl}
        />}
      </div>
    </div>
  );
}

export default ReRegistrationRightBox;
